import { useState, useCallback } from 'react';
import { apiClient } from '../services/api.client';

interface UseDeleteClientReturn {
  deleteClient: (id: string) => Promise<void>;
  loading: boolean;
  error: Error | null;
  reset: () => void;
}

export const useDeleteClient = (): UseDeleteClientReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteClient = useCallback(async (id: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      await apiClient.delete(`clients/${id}`);
    } catch (err) {
      let error: Error;

      if (err && typeof err === 'object' && 'response' in err) {
        try {
          const response = (err as { response: Response }).response;

          // Handle specific status codes
          if (response.status === 401) {
            // Check if this is an auth issue or business logic issue
            const errorData = await response.json();
            error = new Error(
              errorData.message ||
                'You do not have permission to delete this client.'
            );
          } else if (response.status === 409) {
            const errorData = await response.json();
            error = new Error(
              errorData.message || 'Cannot delete client with existing projects'
            );
          } else {
            const errorData = await response.json();
            error = new Error(errorData.message || 'Failed to delete client');
          }
        } catch {
          error = new Error('Failed to delete client');
        }
      } else if (err instanceof Error) {
        error = err;
      } else {
        error = new Error('Failed to delete client');
      }

      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setError(null);
  }, []);

  return {
    deleteClient,
    loading,
    error,
    reset,
  };
};
